import store from '@/store';
import AppModule from '@/store/modules/app';
import axios from 'axios';
import { getModule } from 'vuex-module-decorators';
import NotificationHelper from './notification';

interface IError {
  message: string;
  response: {
    status: number;
    data: any;
  };
}

export interface ILoaderInstance {
  loading?: boolean;
}

export default class HttpHelper {
  static loaderInstance: ILoaderInstance = null;
  static appModule = getModule(AppModule, store);
  static csrfToken = null;

  static set loaderSettings(value: ILoaderInstance) {
    this.loaderInstance = value;
  }

  static get loaderSettings() {
    return this.loaderInstance || {};
  }

  static showLoader(background = false) {
    if (background || this.loaderSettings.loading) {
      return;
    }

    this.appModule.setAppIsLoading(true);
  }

  static async hideLoader() {
    this.appModule.setAppIsLoading(false);
  }

  static authGet<T = any>(url: string, options?): Promise<T> {
    HttpHelper.showLoader(options?.background || false);

    return axios
      .get(url)
      .then((response) => {
        HttpHelper.hideLoader();
        return response.data;
      })
      .catch((err) => HttpHelper.validateResponse(url, err, options));
  }

  static get<T = any>(url: string, options?): Promise<T> {
    HttpHelper.showLoader(options?.background || false);

    return axios
      .get(url)
      .then((response) => {
        HttpHelper.hideLoader();
        return response.data;
      })
      .catch((err) => HttpHelper.validateResponse(url, err, options));
  }

  static getWithStats<T = any>(url: string, options?): Promise<T> {
    HttpHelper.showLoader(options?.background || false);
    const instance = axios.create();

    instance.interceptors.request.use(config => {
      (config as any).metadata = { startTime: new Date() };
      return config;
    }, error => Promise.reject(error));

    instance.interceptors.response.use(response => {
      (response.config as any).metadata.endTime = new Date();
      response.data = {
        latency: (response.config as any).metadata.endTime - (response.config as any).metadata.startTime,
      };
      return response;
    }, error => {
      if (error.config?.metadata) {
        error.config.metadata.endTime = new Date();
        error.latency = error.config.metadata.endTime - error.config.metadata.startTime;
      }
      return Promise.reject(error);
    });

    return instance
      .get(url)
      .then((response) => {
        HttpHelper.hideLoader();
        return response.data;
      })
      .catch((err) => { });
  }

  static post<T = any>(url: string, data: any = {}, options?): Promise<T> {
    HttpHelper.showLoader(options?.background || false);

    return axios
      .post(url, data, HttpHelper.getHeaders())
      .then((response) => {
        HttpHelper.hideLoader();
        return response.data;
      })
      .catch((err) => HttpHelper.validateResponse(url, err, options));
  }

  static delete<T = any>(url: string, options?): Promise<T> {
    HttpHelper.showLoader(options?.background || false);

    return axios
      .delete(url, HttpHelper.getHeaders())
      .then((response) => {
        HttpHelper.hideLoader();
        return response.data;
      })
      .catch((err) => HttpHelper.validateResponse(url, err, options));
  }

  static validateResponse(url: string, err: IError, options?) {
    HttpHelper.hideLoader();

    if (err && options && options.ignoreError) {
      return;
    }

    // request not allowed
    if (err && err.response && err.response.status === 422) {
      NotificationHelper.error(err.response.data.message);
      return;
    }

    if (err && err.response && err.response.status === 403) {
      NotificationHelper.error('Not Allowed');
      return;
    }

    // request unauthorized
    if (err && err.response && err.response.status === 401) {
      return;
    }

    NotificationHelper.error('Something went wrong');
  }

  static getCsrfToken() {
    return this.csrfToken;
  }

  static setCsrfToken(csrfToken) {
    this.csrfToken = csrfToken;
  }

  static getHeaders(headers: any = {}) {
    headers['csrf-token'] = HttpHelper.getCsrfToken();

    return { headers };
  }
}
